import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchAlertGroups } from '../data/alertsApi';
import useScrollPreservation from './useScrollPreservation';

// State preservation utilities
const STATE_STORAGE_KEY = 'technicalAlerts_groupState';
const STATE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

const saveGroupState = (clientId, groupBy, state) => {
  try {
    const stateData = {
      ...state,
      timestamp: Date.now()
    };
    const key = `${STATE_STORAGE_KEY}_${clientId}_${groupBy}`;
    sessionStorage.setItem(key, JSON.stringify(stateData));
  } catch (error) {
    console.warn('Failed to save group state:', error);
  }
};

const loadGroupState = (clientId, groupBy) => {
  try {
    const key = `${STATE_STORAGE_KEY}_${clientId}_${groupBy}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;
    
    const stateData = JSON.parse(stored);
    const isExpired = Date.now() - stateData.timestamp > STATE_EXPIRY_MS;
    
    if (isExpired) {
      sessionStorage.removeItem(key);
      return null;
    }
    
    return stateData;
  } catch (error) {
    console.warn('Failed to load group state:', error);
    return null;
  }
};

const clearGroupState = (clientId, groupBy) => {
  try {
    const key = `${STATE_STORAGE_KEY}_${clientId}_${groupBy}`;
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear group state:', error);
  }
};

/**
 * Hook for fetching technical alerts grouped by strategy
 * Automatically refetches when filters or groupBy strategy changes
 * Uses eager loading - all alerts are fetched upfront, group expansion is UI-only
 */
export default function useTechnicalAlertsGroups(filters, groupBy, initialPage = 1, pageSize = 10) {
  const { currentUser } = useAuth();
  const clientId = currentUser?.customer_id;
  
  // Check for saved state on initialization
  const savedState = clientId && groupBy ? loadGroupState(clientId, groupBy) : null;
  const restoredPage = savedState?.page || initialPage;
  const restoredExpandedGroups = savedState?.expandedGroups ? new Set(savedState.expandedGroups) : new Set();
  const hasRestoredState = !!savedState;
  
  
  const [groups, setGroups] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(restoredPage);
  
  // UI-only expansion state (no API calls)
  const [expandedGroups, setExpandedGroups] = useState(restoredExpandedGroups);
  
  // Track if we've already cleared the restored state
  const restoredStateCleared = useRef(false);

  // Scroll preservation for seamless user experience
  const { restoreScrollState } = useScrollPreservation();

  // Use ref to store latest values to avoid stale closures
  const paramsRef = useRef({ filters, groupBy, page: restoredPage, pageSize });
  paramsRef.current = { filters, groupBy, page, pageSize };


  /**
   * Fetch groups data from the API with all alerts pre-loaded
   */
  const fetchGroups = useCallback(async () => {
    const clientId = currentUser?.customer_id;
    
    if (!clientId) {
      setGroups([]);
      setMetadata(null);
      setPagination(null);
      setLoading(false);
      return;
    }


    setLoading(true);
    setError(null);
    
    try {
      const { filters, groupBy, page, pageSize } = paramsRef.current;
      
      
      // Fetch groups with all alerts pre-loaded
      const response = await fetchAlertGroups(
        clientId,
        filters,
        groupBy,
        page,
        pageSize
      );


      setGroups(response.groups || []);
      setMetadata(response.metadata || null);
      setPagination(response.pagination || null);

      // Restore scroll position after data loads
      restoreScrollState();

      // Clear saved state after successful restoration (only once)
      if (hasRestoredState && !restoredStateCleared.current) {
        clearGroupState(clientId, groupBy);
        restoredStateCleared.current = true;
      }
      
    } catch (error) {
      console.error('Failed to fetch technical alerts groups:', error);
      setError(error);
      setGroups([]);
      setMetadata(null);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.customer_id, clientId, groupBy, hasRestoredState]);

  /**
   * Toggle group expansion (UI-only, no API calls)
   */
  const toggleGroupExpansion = useCallback((groupKey) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  }, []);

  /**
   * Load more groups (next page)
   */
  const loadMoreGroups = useCallback(() => {
    const nextPage = (pagination?.page || 0) + 1;
    const maxPages = pagination?.totalPages || 1;
    
    if (nextPage <= maxPages) {
      // Update params ref with next page
      paramsRef.current = { ...paramsRef.current, page: nextPage };
      fetchGroups();
    }
  }, [pagination, fetchGroups]);

  /**
   * Check if more groups can be loaded
   */
  const canLoadMore = pagination && pagination.page < pagination.totalPages;

  /**
   * Refetch groups data with current parameters
   */
  const refetch = useCallback(() => {
    // Don't reset expanded state or page - preserve both
    // The user should see the same groups expanded after the refetch
    fetchGroups();
  }, [fetchGroups]);

  // Effect to handle initial load and parameter changes
  useEffect(() => {
    const clientId = currentUser?.customer_id;
    if (!clientId) {
      setGroups([]);
      setMetadata(null);
      setPagination(null);
      return;
    }
    fetchGroups();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.customer_id, filters, groupBy, page, pageSize]);

  /**
   * Preserve current state before actions that trigger refetch
   */
  const preserveState = useCallback(() => {
    if (!clientId || !groupBy) {
      return;
    }
    
    // Capture scroll position before saving state
    const scrollContainer = document.querySelector('[data-scroll-container]');
    if (scrollContainer) {
      window.alertScrollState = {
        scrollTop: scrollContainer.scrollTop,
        scrollLeft: scrollContainer.scrollLeft || 0,
        timestamp: Date.now(),
        containerSelector: '[data-scroll-container]'
      };
    }
    
    const stateToSave = {
      page,
      expandedGroups: Array.from(expandedGroups)
    };
    
    saveGroupState(clientId, groupBy, stateToSave);
  }, [clientId, groupBy, page, expandedGroups]);

  /**
   * Navigate to specific page
   */
  const setCurrentPage = useCallback((newPage) => {
    setPage(newPage);
    paramsRef.current = { ...paramsRef.current, page: newPage };
  }, []);

  return { 
    groups, 
    metadata,
    pagination,
    loading, 
    error,
    refetch,
    // UI-only expansion functionality
    expandedGroups,
    toggleGroupExpansion,
    loadMoreGroups,
    canLoadMore,
    // State preservation
    preserveState,
    // Page navigation
    page,
    setCurrentPage
  };
}